import { ReactSVGPanZoom, TOOL_AUTO } from "react-svg-pan-zoom";
import React from "react";
import { ReactSvgPanZoomLoader } from "../utils/ReactSvgPanZoomLoader";
import { latLngToSvgPiecewise } from "../utils/gps/projectionPiecewise.js";
import { focusOnPoint } from "../utils/viewerHelpers.js";
import {
  getCurrentCampusCode,
  getCurrentLocationZoom,
  getGpsMarkerConfig,
  getMapNorthOffsetDeg,
} from "../utils/campusHelpers.js";
import MapEffectComponent from "./MapEffectComponent";
import Button from "@material-ui/core/Button";
import MapDialog from "./MapDialog";
import HeadingPermissionDialog from "./HeadingPermissionDialog";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import campusConfig from "../../json/campus_config.json";
import campusData from "../../json/campus_data.json";
import {
  ensureDeviceOrientationPermission,
  subscribeDeviceHeading,
  isIOS,
} from "../utils/gps/deviceOrientation.js";
import { USE_GPS } from "../../config/gpsConfig";
import { USE_MOCK_GPS, MOCK_LOCATION } from "../../config/gpsDebugConfig";
import {
  GPS_SMOOTHING_ALPHA,
  GPS_DRAW_INTERVAL_MS,
  GPS_COLOR,
} from "../../constants/gpsParams";
import { METERS_PER_DEGREE_LATITUDE } from "../../constants/geodesyConstants";
import { loadMapData } from "../utils/map/loadMapData";
import MapToolbar from "./MapToolbar.jsx";
import { pickNearestCampus } from "../utils/map/findNearestCampus.js";
import SearchPin from "./SearchPin.jsx";
import GpsMarker from "./GpsMarker.jsx";

export const MapModeEnum = { INDOOR: "INDOOR", NORMAL: "NORMAL" };

export default class MapPanZoomWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.Viewer = null;
    this.svgRoot = null;
    this.transformModels = {};
    this.prevSvgPos = {};
    this.headingDeg = null;
    this.unsubscribeHeading = null;

    this.gpsWatchId = null;

    this.lastDraw = 0;
    this.smoothPos = null;

    this.floorStore = new Map();
    this.mountedFloorId = null;
    this.stashedCampuses = new Set();
    this.floorToBuildingCache = new Map();

    this.state = {
      gps: { lat: null, lng: null, accuracy: null },
      isReady: false,
      tool: TOOL_AUTO,
      value: {},
      canRenderMap: false,

      currentCampusID: "",
      currentBuildingID: "",
      currentFloorID: "",
      currentRoomID: "",

      dialogIsOpen: false,
      headingPermissionDialogIsOpen: false,
      selectedMarker: {
        id: "",
        name: "",
        images: [],
        description: "",
        properties: [],
      },
      currentMode: MapModeEnum.NORMAL,
      currentCampus: 1,
      currentCampusCode: null,
      currentMapJson: {},
      currentBuildingArray: [],
      currentObject: null,
      currentSelectedFloors: [],
      currentSelectedFloorIndex: 0,
      currentFloorArray: [],
      currentRoomArray: [],
      currentSearchArray: [],
      mapJson: [],
      mapIconJson: [],
      mapModelJson: [],
      mapSvg: [],

      searchPinVisible: false,
      searchPinX: null,
      searchPinY: null,
      searchPinFloorId: null,
      searchPinBuildingId: null,

      gpsMarkerVisible: false,
      gpsMarkerX: null,
      gpsMarkerY: null,
      gpsAccuracyRadius: 0,
      gpsHeadingDeg: null,
    };
  }

  clearSearchPin = () => {
    this.setState({
      searchPinVisible: false,
      searchPinX: null,
      searchPinY: null,
      searchPinFloorId: null,
      searchPinBuildingId: null,
    });
  };

  updateCurrentCampusCode = () => {
    const currentCampusCode = getCurrentCampusCode(
      this.state.currentCampus,
      campusData
    );
    this.setState({ currentCampusCode });
  };

  dropSearchPinAt = (x, y) => {
    const currentFloorId = this.mountedFloorId;
    const currentBuildingId = this.getCurrentBuildingId();
    this.setState({
      searchPinX: x,
      searchPinY: y,
      searchPinVisible: true,
      searchPinFloorId: currentFloorId,
      searchPinBuildingId: currentBuildingId,
    });
  };

  buildFloorToBuildingCache = () => {
    this.floorToBuildingCache.clear();
    for (const buildingKey of this.state.currentBuildingArray) {
      const building = this.state.currentMapJson[buildingKey];
      if (building?.floors && building.building_id) {
        for (const floor of building.floors) {
          if (floor.floor_id) {
            this.floorToBuildingCache.set(floor.floor_id, building.building_id);
          }
        }
      }
    }
  };

  getCurrentBuildingId = () => {
    const currentFloorId = this.mountedFloorId;
    if (!currentFloorId) return null;
    return this.floorToBuildingCache.get(currentFloorId) ?? null;
  };

  startCompass = async () => {
    const ok = await ensureDeviceOrientationPermission();
    if (!ok) {
      console.warn("DeviceOrientation permission denied");
      return;
    }
    if (this.unsubscribeHeading) this.unsubscribeHeading();
    this.unsubscribeHeading = subscribeDeviceHeading((deg) => {
      this.headingDeg = deg;
      const offset = getMapNorthOffsetDeg(
        campusData,
        this.state.currentCampusCode
      );
      const degToShow = (((deg + offset) % 360) + 360) % 360;
      this.setState({ gpsHeadingDeg: degToShow });
    });
  };

  closeHeadingPermissionDialog = () => {
    this.setState({ headingPermissionDialogIsOpen: false });
  };

  handleApproveHeading = async () => {
    this.setState({ headingPermissionDialogIsOpen: false });
    await this.startCompass();
  };

  /* SVG ロード完了→基準点抽出→モデル前計算 */
  handleSvgReady = (svgRoot) => {
    if (!svgRoot) return;
    this.svgRoot = svgRoot;

    const campus = this.state.currentCampus;

    const pre = this.state.mapModelJson[campus - 1];
    let model = pre;

    this.transformModels[campus] = model;

    this.prevSvgPos[campus] = null;

    this.setState(
      {
        isReady: true,
      },
      () => {
        this.ensureIndoorLayerContainer();
        this.stashFloors();
        if (USE_GPS && USE_MOCK_GPS && model) {
          this.drawGpsMarker(MOCK_LOCATION.lat, MOCK_LOCATION.lng);
        }
      }
    );
  };

  stashFloors = () => {
    const campusIdx = this.state.currentCampus - 1;

    const currentMapJson = this.state.mapJson[campusIdx] || {};
    const buildingKeys = Object.keys(currentMapJson);

    buildingKeys.forEach((key) => {
      const building = currentMapJson[key];
      if (!building?.indoor || !Array.isArray(building.floors)) return;

      building.floors.forEach((f) => {
        const floorElement = document.getElementById(f.floor_id);
        if (!floorElement || !floorElement.parentNode) return;
        floorElement.parentNode.removeChild(floorElement);
        const uniqueFloorKey = `${campusIdx}_${f.floor_id}`;
        if (!this.floorStore.has(uniqueFloorKey)) {
          this.floorStore.set(uniqueFloorKey, floorElement);
        }
      });
    });

    this.stashedCampuses.add(campusIdx);
  };

  resetIndoorLayer = () => {
    const container = this.ensureIndoorLayerContainer();
    if (!container) return;
    // 表示中のフロアをストアへ戻す（DOMから外す）
    while (container.firstChild) {
      const floorElement = container.firstChild;
      const id = floorElement.getAttribute?.("id");
      container.removeChild(floorElement);
      if (id) {
        const campusIdx = this.state.currentCampus - 1;
        const uniqueFloorKey = `${campusIdx}_${id}`;
        this.floorStore.set(uniqueFloorKey, floorElement);
      }
    }
    this.mountedFloorId = null;
  };

  showFloor = (floorId) => {
    const container = this.ensureIndoorLayerContainer();

    if (!container || !floorId) return;
    this.resetIndoorLayer();

    const campusIdx = this.state.currentCampus - 1;
    const uniqueFloorKey = `${campusIdx}_${floorId}`;
    const floorElement = this.floorStore.get(uniqueFloorKey);
    if (!floorElement) return;

    container.appendChild(floorElement);
    this.floorStore.delete(uniqueFloorKey);
    this.mountedFloorId = floorId;
  };

  requestInitialPosition = () => {
    if (!USE_GPS) {
      this.setState({ canRenderMap: true });
      return;
    }

    if (USE_MOCK_GPS) {
      const nearest = pickNearestCampus(
        MOCK_LOCATION.lat,
        MOCK_LOCATION.lng,
        campusData
      );
      if (Number.isInteger(nearest)) {
        this.setState({ currentCampus: nearest }, () => {
          this.updateGps(
            MOCK_LOCATION.lat,
            MOCK_LOCATION.lng,
            MOCK_LOCATION.acc
          );
          this.setState({ canRenderMap: true });
        });
        return;
      }
      this.updateGps(MOCK_LOCATION.lat, MOCK_LOCATION.lng, MOCK_LOCATION.acc);
      this.setState({ canRenderMap: true });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const nearest = pickNearestCampus(latitude, longitude, campusData);
        if (Number.isInteger(nearest) && nearest !== this.state.currentCampus) {
          this.setState({ currentCampus: nearest }, () => {
            this.updateGps(latitude, longitude, accuracy);
            this.setState({ canRenderMap: true });
          });
          return;
        }
        this.updateGps(latitude, longitude, accuracy);
        this.setState({ canRenderMap: true });
      },
      (err) => {
        console.warn("[GPS fast init error]", err);
        this.setState({ currentCampus: 1, canRenderMap: true });
      },
      { enableHighAccuracy: false, maximumAge: Infinity, timeout: 2000 }
    );
  };

  /* 現在地監視 */
  componentDidMount() {
    if (this.Viewer) this.Viewer.fitToViewer();
    if (!USE_GPS) return;

    this.requestInitialPosition();
    // iOS のときだけ「方位取得の許可」ダイアログを表示
    if (isIOS() && !this.unsubscribeHeading) {
      const hasOrientation =
        typeof window !== "undefined" && window.DeviceOrientationEvent;

      if (hasOrientation) {
        // iOS の場合は requestPermission があるかどうかで判定
        if (
          typeof window.DeviceOrientationEvent.requestPermission === "function"
        ) {
          window.DeviceOrientationEvent.requestPermission()
            .then((state) => {
              if (state === "granted") {
                this.setState({ headingPermissionDialogIsOpen: false });
                this.startCompass();
              } else {
                this.setState({ headingPermissionDialogIsOpen: false });
              }
            })
            .catch(() => {
              this.setState({ headingPermissionDialogIsOpen: true });
            });
        } else {
          // 古いiOS (requestPermissionなし)で常に利用可能とみなす
          this.setState({ headingPermissionDialogIsOpen: false });
        }
      }
    } else {
      this.startCompass();
    }

    if (USE_MOCK_GPS) {
      this.updateGps(MOCK_LOCATION.lat, MOCK_LOCATION.lng, MOCK_LOCATION.acc);
      return;
    }

    // 位置が変化したときにだけコールバックを発火する APIを用いて、更新する。
    this.gpsWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        this.updateGps(latitude, longitude, accuracy);
      },
      (err) => console.error("[GPS watch error]", err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
  }

  componentWillUnmount() {
    if (USE_GPS && this.gpsWatchId !== null)
      navigator.geolocation.clearWatch(this.gpsWatchId);
    if (this.unsubscribeHeading) this.unsubscribeHeading();
  }

  ensureIndoorLayerContainer = () => {
    if (!this.svgRoot) return null;
    let container = this.svgRoot.querySelector("#indoor-layer");

    if (!container) {
      container = document.createElementNS("http://www.w3.org/2000/svg", "g");
      container.setAttribute("id", "indoor-layer");
      this.svgRoot.appendChild(container);
    }

    return container;
  };

  /* SVG 座標へ変換して移動 */
  drawGpsMarker = (lat, lng) => {
    if (!USE_GPS) return null;
    const campus = this.state.currentCampus;
    const model = this.transformModels[campus];
    if (!this.svgRoot || !model) return;
    const svgPt = latLngToSvgPiecewise(lat, lng, model);

    if (!svgPt) {
      this.setState({ gpsMarkerVisible: false });
      return;
    }

    const accuracy = this.state?.gps?.accuracy;

    // 微小移動はスキップ
    const prev = this.prevSvgPos[campus];
    const microMove =
      prev && Math.hypot(svgPt.x - prev.x, svgPt.y - prev.y) < 0.8;

    if (!prev || !microMove) {
      this.prevSvgPos[campus] = svgPt;

      let accuracyRadius = 0;
      if (accuracy != null && accuracy > 0) {
        const offsetLat = lat + accuracy / METERS_PER_DEGREE_LATITUDE;
        const accuracyEdgeSvgPt = latLngToSvgPiecewise(offsetLat, lng, model);
        if (accuracyEdgeSvgPt) {
          accuracyRadius = Math.abs(accuracyEdgeSvgPt.y - svgPt.y);
          if (accuracyRadius > 150) accuracyRadius = 0; // 精度が悪すぎる場合は非表示
        }
      }

      this.setState({
        gpsMarkerVisible: true,
        gpsMarkerX: svgPt.x,
        gpsMarkerY: svgPt.y,
        gpsAccuracyRadius: accuracyRadius,
      });
    }
  };

  /* GPS値更新：EMA平滑化＋20fpsに間引きして描画 */
  updateGps = (lat, lng, accuracy) => {
    if (!USE_GPS) return null;
    const now = performance.now?.() ?? Date.now();
    if (now - this.lastDraw < GPS_DRAW_INTERVAL_MS) {
      // 20fps
      // 状態だけ更新（パネルの数値用）
      this.setState({ gps: { lat, lng, accuracy } });
      return;
    }
    this.lastDraw = now;
    this.smoothPos = this.smoothPos
      ? {
          lat:
            this.smoothPos.lat +
            GPS_SMOOTHING_ALPHA * (lat - this.smoothPos.lat),
          lng:
            this.smoothPos.lng +
            GPS_SMOOTHING_ALPHA * (lng - this.smoothPos.lng),
        }
      : { lat, lng };

    this.setState({ gps: { lat, lng, accuracy } }, () => {
      this.drawGpsMarker(this.smoothPos.lat, this.smoothPos.lng);
    });
  };

  generateFloorRoomArray = () => {
    const buildings = this.state.currentBuildingArray
      .map((value) => this.state.currentMapJson[value])
      .filter(Boolean);

    const currentFloorArray = buildings.flatMap((value) =>
      Array.isArray(value.floors) ? value.floors : []
    );

    const currentRoomArray = currentFloorArray.flatMap((value) =>
      Array.isArray(value.rooms) ? value.rooms : []
    );

    const buildingSearchItems = buildings.map((b) => ({
      building_name: b.name || "",
      building_id: b.building_id,
      building_pin: b.building_pin,
      building_base: b.building_base,
      isBuilding: true,
    }));

    const currentSearchArray = [...buildingSearchItems, ...currentRoomArray];
    this.setState({ currentFloorArray, currentRoomArray, currentSearchArray });
  };

  //クリックされた先のデータが一体何者かを取得
  getClickedObjectDetail = (target_id) => {
    console.log("bbbb", target_id);
    //buildingの場合
    const buildingDetail = this.state.currentBuildingArray.find((value) => {
      return (
        this.state.currentMapJson[value].building_pin === target_id ||
        this.state.currentMapJson[value].building_base === target_id
      );
    });
    console.log("building" + buildingDetail);
    if (buildingDetail) {
      return {
        type: "building",
        detail: this.state.currentMapJson[buildingDetail],
      };
    }
    //floorをクリックした
    const floorDetail = this.state.currentFloorArray.find((value) => {
      return value.floor_id === target_id;
    });
    if (floorDetail) {
      return {
        type: "floor",
        detail: floorDetail,
      };
    }
    //部屋をクリックした
    const roomDetail = this.state.currentRoomArray.find((value) => {
      return value.room_id === target_id;
    });
    if (roomDetail) {
      return {
        type: "room",
        detail: roomDetail,
      };
    }
    return {
      type: "others",
    };
  };

  //SVGを読み込んだ後の初期化処理部分
  initializeMap = () => {
    const campusIdx = this.state.currentCampus - 1;
    const currentMapJson = this.state.mapJson[campusIdx] || {};
    const currentBuildingArray = Object.keys(currentMapJson);
    this.setState(
      {
        currentMapJson,
        currentBuildingArray,
      },
      () => {
        this.generateFloorRoomArray();
        this.updateCurrentCampusCode();
        this.buildFloorToBuildingCache();
      }
    );
    this.resetMapParameter();
  };

  resetMapParameter = () => {
    this.setState({
      currentMode: MapModeEnum.NORMAL,
      currentObject: null,
      currentSelectedFloors: [],
      currentSelectedFloorIndex: 0,
    });
    this.Viewer.reset();
    this.resetIndoorLayer();
  };

  panSvgHandler = (clientRect) => {
    const rectLeft = clientRect.x;
    const rectTop = clientRect.y;
    const screenCX = rectLeft + clientRect.width / 2;
    const screenCY = rectTop + clientRect.height / 2;

    const { a: scale, e: tx, f: ty } = this.Viewer.getValue();
    const viewerCX = (screenCX - tx) / scale;
    const viewerCY = (screenCY - ty) / scale;
    this.Viewer.setPointOnViewerCenter(viewerCX, viewerCY, 6.0);

    const pt = this.svgRoot.createSVGPoint();
    pt.x = screenCX;
    pt.y = screenCY;

    const screenCTM = this.svgRoot.getScreenCTM();
    const svgP = pt.matrixTransform(screenCTM.inverse());

    this.clearSearchPin?.();
    this.dropSearchPinAt?.(svgP.x, svgP.y);
  };

  changeTool = (nextTool) => {
    this.setState({
      tool: nextTool,
    });
  };

  changeValue = (nextValue) => {
    this.setState({ value: nextValue });
  };

  // フロアが変更された時のイベント
  changeFloor = (changeToIndex) => {
    this.setState({ currentSelectedFloorIndex: changeToIndex });
    const target = this.state.currentSelectedFloors[changeToIndex];
    if (target?.floor_id) {
      this.showFloor(target.floor_id);
      this.updateSearchPinVisibility(target.floor_id);
    }
  };

  updateSearchPinVisibility = (currentFloorId) => {
    const { searchPinFloorId, searchPinBuildingId, searchPinX, searchPinY } =
      this.state;

    if (
      searchPinFloorId !== null &&
      searchPinBuildingId !== null &&
      searchPinX !== null &&
      searchPinY !== null
    ) {
      const currentBuildingId = this.getCurrentBuildingId();
      if (currentBuildingId === searchPinBuildingId) {
        if (currentFloorId === searchPinFloorId) {
          this.setState({ searchPinVisible: true });
        } else {
          this.setState({ searchPinVisible: false });
        }
      } else {
        this.setState({ searchPinVisible: true });
      }
    }
  };

  handleSelect = (target_id, isBuildingSearch) => {
    if (!target_id) return;
    if (isBuildingSearch) {
      if (this.state.currentMode === MapModeEnum.INDOOR) {
        this.resetIndoorLayer();
        this.setState({
          currentMode: MapModeEnum.NORMAL,
          currentSelectedFloors: [],
          currentSelectedFloorIndex: 0,
        });
      } else if (this.state.currentMode !== MapModeEnum.NORMAL) {
        this.setState({ currentMode: MapModeEnum.NORMAL });
      }
      const elem = document.getElementById(target_id);
      if (elem) {
        const cr = elem.getBoundingClientRect();
        this.panSvgHandler(cr);
      }
      return;
    }

    //クリックされたobjectの判断 building,floor,room の３種
    const objectDetail = this.getClickedObjectDetail(target_id);
    console.log(objectDetail);
    const building_id =
      objectDetail.type === "others" ? "" : objectDetail.detail.building_id;
    const floor_id =
      objectDetail.type === "others" ? "" : objectDetail.detail.floor_id;
    this.state.currentBuildingArray.forEach((value, index) => {
      //indoor用のビルの場合
      if (this.state.currentMapJson[value].building_id === building_id) {
        if (this.state.currentMapJson[value].indoor) {
          this.setState({
            currentMode: MapModeEnum.INDOOR,
            currentSelectedFloors: this.state.currentMapJson[value].floors, //フロアリストを保持
          });
          const floors = this.state.currentMapJson[value].floors || [];
          const foundIndex = floors.findIndex((f) => f.floor_id === floor_id);
          const selectedIndex = foundIndex >= 0 ? foundIndex : 0;
          const selectedFloor = floors[selectedIndex];

          this.setState({ currentSelectedFloorIndex: selectedIndex });

          if (selectedFloor?.floor_id) {
            this.showFloor(selectedFloor.floor_id);
            this.updateSearchPinVisibility(selectedFloor.floor_id);
          }

          //indoorの時だけ中央に寄せるように調整
          //const cr = viewerEvent.originalEvent.target.getBoundingClientRect();
          let elem = document.getElementById(target_id);
          if (elem) {
            const cr = elem.getBoundingClientRect();
            this.panSvgHandler(cr);
          }
        } else {
          this.setState({ currentMode: MapModeEnum.NORMAL });
        }
      }
    });
  };

  handleClick = (target_id) => {
    console.log(
      "mapIconJSon",
      this.state.mapIconJson[this.state.currentCampus - 1]
    );
    const markers =
      this.state.mapIconJson[this.state.currentCampus - 1].markers;
    //const target_id = viewerEvent.originalEvent.target.id;
    //target_idが存在しない場合は処理する必要のないmapのSVG領域
    if (target_id) {
      //クリックされたobjectの判断 building,floor,room の３種
      const objectDetail = this.getClickedObjectDetail(target_id);
      console.log(objectDetail);

      this.state.currentBuildingArray.forEach((value) => {
        //indoor用のビルの場合
        if (
          this.state.currentMapJson[value].building_pin === target_id ||
          this.state.currentMapJson[value].building_base === target_id
        ) {
          if (this.state.currentMapJson[value].indoor) {
            this.setState({
              currentMode: MapModeEnum.INDOOR,
              currentSelectedFloors: this.state.currentMapJson[value].floors, //フロアリストを保持
            });
            const floors = this.state.currentMapJson[value].floors || [];
            let selectedIndex = 0;
            if (
              this.state.searchPinVisible &&
              this.state.searchPinBuildingId ===
                this.state.currentMapJson[value].building_id
            ) {
              const pinFloorIndex = floors.findIndex(
                (f) => f.floor_id === this.state.searchPinFloorId
              );
              selectedIndex = pinFloorIndex;
            } else {
              const firstFloorIndex = floors.findIndex((f) =>
                /_1f|-1f/i.test(f.floor_id)
              );
              selectedIndex = firstFloorIndex >= 0 ? firstFloorIndex : 0;
            }
            const selectedFloor = floors[selectedIndex];
            this.setState({ currentSelectedFloorIndex: selectedIndex });
            if (selectedFloor?.floor_id) {
              this.showFloor(selectedFloor.floor_id);
              this.updateSearchPinVisibility(selectedFloor.floor_id);
            }
          } else {
            this.setState({ currentMode: MapModeEnum.NORMAL });
          }
        } else {
        }
      });

      markers.some((marker) => {
        if (target_id === marker.id) {
          this.setState({
            dialogIsOpen: true,
            selectedMarker: marker,
          });
          return true;
        }
        return false;
      });
    }
  };

  //＋ボタンのタップ時
  handleZoomIn = () => {
    this.Viewer.zoomOnViewerCenter(1.2);
  };

  handleZoomOut = () => {
    this.Viewer.zoomOnViewerCenter(0.8);
  };
  handleZoomChange = (zoomV) => {
    // ズームアウト時の自動インドマップ解除を無効に
    // if (zoomV.a <= 2.5 && this.state.currentMode === MapModeEnum.INDOOR) {
    //     this.state.currentSelectedFloors.forEach((value, index) => {
    //         if (document.getElementById(value.floor_id)) {
    //             document.getElementById(value.floor_id).classList.add("hidden");
    //         }
    //     })
    //     //indoor関係のデータを初期化
    //     this.setState({
    //         currentMode: MapModeEnum.NORMAL,
    //         currentSelectedFloors: [],
    //         currentSelectedFloorIndex: 0
    //     })
    // }
  };

  centerOnCurrentGps = () => {
    if (!USE_GPS || !this.state.gpsMarkerVisible) return;

    const zoomLevel = getCurrentLocationZoom(
      campusConfig,
      this.state.currentCampusCode
    );
    focusOnPoint(this.Viewer, zoomLevel, "gps-marker");
  };

  render() {
    const { props } = this;

    if (USE_GPS && !this.state.canRenderMap) {
      return null;
    }

    const indoorChanger =
      this.state.currentMode === MapModeEnum.INDOOR ? (
        <ButtonGroup
          orientation="vertical"
          color="secondary"
          size="small"
          aria-label="vertical contained primary button group "
          variant="contained"
        >
          {this.state.currentSelectedFloors.map((value, index) => {
            return (
              <Button
                color={
                  this.state.currentSelectedFloorIndex === index
                    ? "primary"
                    : null
                }
                onClick={() => this.changeFloor(index)}
                key={index}
              >
                {value.floor_name}
              </Button>
            );
          })}
        </ButtonGroup>
      ) : null;
    if (this.state.mapSvg.length === 0) {
      loadMapData().then((data) => {
        this.setState({
          mapSvg: data.mapSvg,
          mapJson: data.mapJson,
          mapIconJson: data.mapIconJson,
          mapModelJson: data.mapModelJson,
        });
      });
      return null;
    }

    return (
      <>
        <MapDialog
          onClose={() => {
            this.setState({ dialogIsOpen: false });
          }}
          open={this.state.dialogIsOpen}
          marker={this.state.selectedMarker}
        />
        <HeadingPermissionDialog
          open={this.state.headingPermissionDialogIsOpen}
          onClose={this.closeHeadingPermissionDialog}
          onApprove={this.handleApproveHeading}
        />
        <MapEffectComponent
          isReady={this.state.isReady}
          initializeMap={this.initializeMap}
        />
        <ReactSvgPanZoomLoader
          src={this.state.mapSvg[this.state.currentCampus - 1]}
          onSVGReady={this.handleSvgReady}
          render={(content) => (
            <ReactSVGPanZoom
              background={"#F4F4F4"}
              SVGBackground={"#F4F4F4"}
              ref={(Viewer) => (this.Viewer = Viewer)}
              width={props.width}
              height={props.height}
              tool={this.state.tool}
              onChangeTool={this.changeTool}
              value={this.state.value}
              toolbarProps={{ position: "none" }}
              miniatureProps={{ position: "none" }}
              scaleFactor={1.5}
              scaleFactorOnWheel={1.2}
              scaleFactorMin={1.0}
              preventPanOutside={true}
              detectAutoPan={false}
              disableDoubleClickZoomWithToolAuto={false}
              onClick={(viewerEvent) =>
                this.handleClick(viewerEvent.originalEvent.target.id)
              }
              onTouchStart={(viewerEvent) =>
                this.handleClick(viewerEvent.originalEvent.target.id)
              }
              onZoom={(zoomValue) => this.handleZoomChange(zoomValue)}
              onPan={(value) => this.handleZoomChange(value)}
              onChangeValue={this.changeValue}
            >
              <svg width={props.width} height={props.height}>
                {content}
              </svg>
            </ReactSVGPanZoom>
          )}
        />
        <MapToolbar
          handleClick={this.handleSelect}
          handleZoomIn={this.handleZoomIn}
          handleZoomOut={this.handleZoomOut}
          handleCenterOnGps={this.centerOnCurrentGps}
          clearSearchPin={this.clearSearchPin}
          useGps={USE_GPS}
          currentCampus={this.state.currentCampus}
          changeCampus={(value) =>
            this.setState({
              currentCampus: value,
              isReady: false,
              searchPinVisible: false,
              searchPinX: null,
              searchPinY: null,
              searchPinFloorId: null,
              searchPinBuildingId: null,
              gpsMarkerVisible: false,
              gpsMarkerX: null,
              gpsMarkerY: null,
              gpsAccuracyRadius: 0,
              gpsHeadingDeg: null,
            })
          }
          listArray={this.state.currentSearchArray}
        >
          {indoorChanger}
        </MapToolbar>
        <SearchPin
          svgRoot={this.svgRoot}
          x={this.state.searchPinX}
          y={this.state.searchPinY}
          visible={this.state.searchPinVisible}
          zoom={this.state.value?.a ?? 1}
        />
        <GpsMarker
          svgRoot={this.svgRoot}
          x={this.state.gpsMarkerX}
          y={this.state.gpsMarkerY}
          visible={this.state.gpsMarkerVisible}
          zoom={this.state.value?.a ?? 1}
          accuracy={this.state.gps?.accuracy}
          accuracyRadius={this.state.gpsAccuracyRadius}
          headingDeg={this.state.gpsHeadingDeg}
          options={(() => {
            const code =
              this.state.currentCampusCode ||
              getCurrentCampusCode(this.state.currentCampus, campusData);
            const markerCfg = getGpsMarkerConfig(campusData, code);
            const coneCfg = markerCfg?.headingCone;
            return {
              color: GPS_COLOR,
              showPulse: true,
              headR: markerCfg?.headR,
              pulseR: markerCfg?.pulseR,
              headingCone: {
                length: coneCfg?.length,
                width: coneCfg?.width,
                spreadDeg: coneCfg?.spreadDeg,
                opacity: coneCfg?.opacity,
                fill: GPS_COLOR,
              },
            };
          })()}
        />
      </>
    );
  }
}
